import { useEffect, useState } from 'react';
import { Nav } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { Bar } from 'react-chartjs-2';
import { Form } from 'react-bootstrap';
import {
    Chart as ChartJS,
    BarElement,
    CategoryScale,
    LinearScale,
    Tooltip,
    Legend,
} from 'chart.js';
// css
import './AdminDashboard.css';
// custom imports
import TopInspectors from './TopInspectors';
import DashboardStatsService from "../../Services/DashboardStats/DashbordStatsService"
import { HandleApiError } from '../../Utils/CommonErrors/HandleApiError';
import { hideLoader, showLoader } from '../../Redux/Slice/Loader/loaderSlice';
import showToast from '../../Utils/Toast/ToastNotification';
// Register ChartJS modules
ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const AdminDashboard = () => {
    const dispatch = useDispatch()
    const token = useSelector((state) => state.auth?.user?.data?.body?.data?.token)
    const [key, setKey] = useState('weekdays');
    const [status, setStatus] = useState("Complete");
    const [filterType, setFilterType] = useState("last7days");
    const [result, setResult] = useState("")
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    const defaultLabels = [
        "CAAS", "Expert Services", "PCDI", "SAS", "SBBIS", "SOCA", "TIS"
    ];
    const [labels, setLabels] = useState(defaultLabels);
    const [datavalues, setData] = useState(Array(defaultLabels.length).fill(0));

    // dashboard contents
    const getData = async () => {
        dispatch(showLoader());
        const params = {
            status: status,
            filterType: filterType,
            startDate: fromDate,
            endDate: toDate
        }
        try {
            const response = await DashboardStatsService.dashboardConent(
                params, token
            );
            if (response?.data?.statusCode == 200) {
                const data = response.data.body.data
                const responseData = response.data.body.data.GraphData || [];
                const countMap = {};
                responseData.forEach(item => {
                    countMap[item.project_type] = item.inspection_count;
                });

                const updatedData = defaultLabels.map(label => countMap[label] || 0);

                setResult(data) //count of the inspection
                setLabels(defaultLabels);
                setData(updatedData);
            } else {
                showToast("error", response.data.body?.message);
            }
        } catch (error) {
            await HandleApiError(error);
        } finally {
            dispatch(hideLoader());
        }
    };

    // patch data from the apis to the fields
    const data = {
        labels: labels,
        datasets: [
            {
                label: 'Total Inspections',
                data: datavalues,
                backgroundColor: '#B6D79B',
                borderRadius: 5,
            },
        ],
    };

    const options = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    font: {
                        size: 14,
                    },
                },
            },
            tooltip: {
                enabled: true,
                callbacks: {
                    label: function (context) {
                        return `Total: ${context.raw ?? 0}`;
                    },
                },
                backgroundColor: '#333',
                titleFont: { size: 14 },
                bodyFont: { size: 12 },
            },
        },
        interaction: {
            mode: 'index',
            intersect: false, // important for 0 bars
        },
        scales: {
            x: {
                ticks: {
                    font: {
                        size: 13,
                    },
                },
            },
            y: {
                beginAtZero: true,
                ticks: {
                    font: {
                        size: 13,
                    },
                },
            },
        },
        hover: {
            mode: 'nearest',
            intersect: true,
        },
    };

    useEffect(() => {
        getData();
    }, [status, filterType, fromDate, toDate]);

    return (
        <>
            <div className="main-inner-block">
                <div className="content">
                    <div className="d-flex align-items-center justify-content-between mb-4 block-one gap-3">
                        <div className="heading-content ms-0 mt-0">Dashboard</div>
                    </div>

                    <div className="white-box bg-box-mob">
                        <div className="dashboard-header">
                            <div className="dashboard-header-items">
                                <div className="date-item form-item">
                                    <label className="form-label">From date</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        id="inspectionDate_from"
                                        name="inspectionDate_from"
                                        placeholder="From"
                                        value={fromDate}
                                        onChange={(e) => {
                                            setFromDate(e.target.value);
                                            setFilterType("custom");
                                        }}

                                    />{" "}
                                </div>
                                <div className="date-item form-item">
                                    <label className="form-label">To date</label>

                                    <input
                                        type="date"
                                        className="form-control"
                                        id="inspectionDate_to"
                                        name="inspectionDate_to"
                                        placeholder="To"
                                        value={toDate}
                                        onChange={(e) => {
                                            setToDate(e.target.value);
                                            setFilterType("custom");
                                        }}
                                        disabled={!fromDate}
                                    />
                                </div>
                            </div>
                            <div className="dashboard-action">
                                <div className="dashboard-tabs d-flex">
                                    <Nav
                                        variant="tabs"
                                        activeKey={key}
                                        onSelect={
                                            (selectedKey) => {
                                                setKey(selectedKey)
                                                if (selectedKey === "weekdays") {
                                                    setFilterType("last7days");
                                                    setFromDate("");
                                                    setToDate("");
                                                } else if (selectedKey === "monthdays") {
                                                    setFilterType("last28days");
                                                    setFromDate("");
                                                    setToDate("");
                                                }
                                            }
                                        }
                                        justify
                                    >
                                        <Nav.Item>
                                            <Nav.Link eventKey="weekdays">7 Days</Nav.Link>
                                        </Nav.Item>
                                        <Nav.Item>
                                            <Nav.Link eventKey="monthdays">28 Days</Nav.Link>
                                        </Nav.Item>

                                    </Nav>
                                </div>

                                {/* Filter */}
                                <div className="filter d-flex align-items-center gap-4">
                                    <Form.Select
                                        onChange={(e) => setStatus(e.target.value)}
                                        className="filter-select"
                                        aria-label="Status filter"
                                    >

                                        <option value="Complete" selected>Complete</option>
                                        <option value="Scheduled">Scheduled</option>
                                        <option value="Not Started">Not Started</option>
                                        <option value="Archive">Archive</option>
                                        <option value="In Progress">In Progress</option>
                                    </Form.Select>
                                </div>

                            </div>
                        </div>
                        <div className="tab-contents">
                            {key === 'weekdays' && (
                                <div className="dashboard-wrapper">
                                    <div className="dashboard-cards">
                                        <div className="dashboard-card-items">
                                            <p className="dashboard-card-indicator">{result.totalScheduledInspection}</p>
                                            <p className="dash-card-text">Total Number of Inspections scheduled by Date Filter</p>
                                        </div>
                                        <div className="dashboard-card-items">
                                            <p className="dashboard-card-indicator">{result.totalInspectionSubmitted}</p>
                                            <p className="dash-card-text">Total Number of Inspections Submitted by Date Filter</p>
                                        </div>
                                    </div>


                                    <div className="dashboard-chart-row">
                                        <div className="dashboard-charts dashboard-border-shadow">

                                            <div className="dashboard-chart-item">
                                                <h3 className="dashboard-chart-heading">Inspection by project type</h3>
                                                <Bar data={data} options={options} />
                                            </div>
                                        </div>
                                        <div className="dashboard-charts dashboard-border-shadow">
                                            <div className="dashboard-chart-item">
                                                <h3 className="dashboard-chart-heading">Top Active Inspector</h3>
                                                <TopInspectors filterType={filterType} fromDate={fromDate} toDate={toDate} options={options} />
                                            </div>
                                        </div>
                                    </div>


                                </div>
                            )}
                            {key === 'monthdays' && (
                                <div className="dashboard-wrapper">
                                    <div className="dashboard-cards">
                                        <div className="dashboard-card-items">
                                            <p className="dashboard-card-indicator">{result.totalScheduledInspection}</p>
                                            <p className="dash-card-text">Total Number of Inspections Scheduled by Date Filter</p>
                                        </div>
                                        <div className="dashboard-card-items">
                                            <p className="dashboard-card-indicator">{result.totalInspectionSubmitted}</p>
                                            <p className="dash-card-text">Total Number of Inspections Submitted by Date Filter</p>
                                        </div>
                                    </div>

                                    <div className="dashboard-chart-row">

                                        <div className="dashboard-charts dashboard-border-shadow">
                                            <div className="dashboard-chart-item">
                                                <h3 className="dashboard-chart-heading">Inspection by project type</h3>
                                                <Bar data={data} options={options} />
                                            </div>
                                        </div>
                                        <div div className="dashboard-charts dashboard-border-shadow">
                                            <div className="dashboard-chart-item">
                                                <h3 className="dashboard-chart-heading">Top Active Inspector</h3>
                                                <TopInspectors filterType={filterType} fromDate={fromDate} toDate={toDate} options={options} />
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            )}

                        </div>
                    </div>

                </div>
            </div >
        </>
    )
}

export default AdminDashboard